import React from "react";
import TrinetraLicenseGenerationForm from "../../../components/forms/TrinetraLicenseGenerationForm";
import ChipList from "../../../components/common/ChipList";
import { Download, Trash2, Pencil } from "lucide-react";

const PAGE_SIZE = 10;
const CLIENT_DROPDOWN_LIMIT = 1000;

const buildQueryString = (params) => {
	const q = new URLSearchParams();
	Object.entries(params).forEach(([k, v]) => {
		if (v !== undefined && v !== null && v !== "") q.append(k, v);
	});
	return q.toString();
};

const getDerivedStatus = (license) => {
	if (license.expiresAt && new Date(license.expiresAt) <= new Date()) return "EXPIRED";
	return "ACTIVE";
};

export const createTrinetraProductHandler = ({
	callApi,
	API_CONFIG,
	showError,
	showSuccess,
	getReadableErrorMessage,
}) => {
	const DEFAULT_FILTERS = {
		search: "",
		licenseType: "all",
		status: "all",
		page: 1,
		limit: PAGE_SIZE,
	};

	const loadFilterData = async () => {
		try {
			const configRes = await callApi(
				API_CONFIG.TRINETRA_LICENSE.CONFIG.METHOD,
				API_CONFIG.TRINETRA_LICENSE.CONFIG.ENDPOINT
			);

			const cfg = configRes?.data?.data || {};

			return {
				ok: true,
				data: {
					availableModels: cfg.availableModels || [],
					availableFeatures: cfg.availableFeatures || [],
					featureCatalog: cfg.featureCatalog || [],
				},
			};
		} catch (error) {
			showError(getReadableErrorMessage(error));
			return { ok: false };
		}
	};

	const getFilterDefinitions = (filterMetadata = {}) => [
		{
			key: "allowedModels",
			label: "Allowed Models",
			type: "select",
			options: [
				{ value: "all", label: "All Models" },
				...(filterMetadata.availableModels || []).map((item) => ({
					value: item,
					label: item,
				})),
			],
		},
		{
			key: "features",
			label: "Features",
			type: "select",
			options: [
				{ value: "all", label: "All Features" },
				// Catalogued features get their display label; anything issued
				// as a custom feature is not in the catalog, so it falls back to
				// the raw key.
				...(filterMetadata.availableFeatures || []).map((item) => ({
					value: item,
					label:
						(filterMetadata.featureCatalog || []).find((f) => f.key === item)
							?.label || item,
				})),
			],
		},
	];

	const columns = ({ onDownload, onDelete, onEdit }) => [
		{
			key: "licenseId",
			label: "License ID",
			render: (row) => (
				<span
					className="text-xs font-medium"
					style={{ color: "var(--main-text)" }}
				>
					{row.licenseId || "—"}
				</span>
			),
		},
		{
			key: "description",
			label: "Description",
			// Capped and truncated rather than wrapped: 200 characters of free
			// text would otherwise set the height of the whole row. The full
			// text stays available on hover.
			render: (row) =>
				row.description ? (
					<span
						className="block max-w-[220px] truncate text-xs"
						style={{ color: "var(--main-text)" }}
						title={row.description}
					>
						{row.description}
					</span>
				) : (
					<span className="text-xs" style={{ color: "var(--main-text-muted)" }}>
						—
					</span>
				),
		},
		{
			key: "client",
			label: "Client Name",
			render: (row) => (
				<span
					className="text-xs font-medium"
					style={{ color: "var(--main-text)" }}
				>
					{row.clientObjectId?.clientName || "—"}
				</span>
			),
		},
		{
			key: "type",
			label: "Type",
			render: (row) => {
				const isTrial = row.licenseType === "TRIAL";

				return (
					<span
						className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold border"
						style={{
							background: isTrial
								? "var(--license-trial-bg)"
								: "var(--license-perpetual-bg)",
							color: isTrial
								? "var(--license-trial-text)"
								: "var(--license-perpetual-text)",
							borderColor: isTrial
								? "var(--license-trial-border)"
								: "var(--license-perpetual-border)",
						}}
					>
						{row.licenseType}
					</span>
				);
			},
		},
		{
			key: "cameras",
			label: "Cameras",
			render: (row) => row.maxCameras ?? "—",
		},
		{
			key: "aiModels",
			label: "AI Models",
			render: (row) => row.maxAiModels ?? "—",
		},
		{
			key: "nodes",
			label: "Nodes",
			render: (row) => row.maxNodes ?? "—",
		},
		{
			key: "allowedModels",
			label: "Allowed Models",
			render: (row) => (
				// customModels marks which names were not in the standard list,
				// so those chips read as muted rather than as first-class models.
				<ChipList
					items={row.allowedModels || []}
					muted={new Set(row.customModels || [])}
					mutedTitle="Custom model"
					max={3}
					palette={{
						bg: "var(--chip-model-bg)",
						text: "var(--chip-model-text)",
						border: "var(--chip-model-border)",
					}}
				/>
			),
		},
		{
			key: "features",
			label: "Features",
			render: (row) => (
				// customFeatures marks which keys were not in the catalog, so
				// those chips read as muted rather than as catalogued features.
				<ChipList
					items={row.trinetraFeatures || []}
					muted={new Set(row.customFeatures || [])}
					mutedTitle="Custom feature"
					max={4}
					palette={{
						bg: "var(--chip-feature-bg)",
						text: "var(--chip-feature-text)",
						border: "var(--chip-feature-border)",
					}}
				/>
			),
		},
		{
			key: "customParameters",
			label: "Custom Params",
			render: (row) => (
				<ChipList
					items={(row.customParameters || []).map(
						({ key, value }) =>
							`${key}=${Array.isArray(value) ? value.join(",") : String(value)}`
					)}
					max={2}
					palette={{
						bg: "transparent",
						text: "var(--main-text-muted)",
						border: "var(--card-border)",
					}}
				/>
			),
		},
		{
			key: "status",
			label: "Status",
			render: (row) => {
				const status = getDerivedStatus(row);
				return (
					<span
						className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${status === "ACTIVE"
							? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20"
							: "bg-rose-500/10 text-rose-500 border border-rose-500/20"
							}`}
					>
						{status}
					</span>
				);
			},
		},
		{
			key: "expiresAt",
			label: "Expiry",
			render: (row) => (
				<span
					className="text-xs font-medium"
					style={{ color: "var(--main-text-muted)" }}
				>
					{row.expiresAt
						? new Date(row.expiresAt).toLocaleDateString("en-GB")
						: "Never"}
				</span>
			),
		},
		{
			key: "actions",
			label: "Actions",
			render: (row) => (
				<div className="flex items-center gap-2">
					<button
						onClick={(e) => {
							e.stopPropagation();
							if (onDownload) onDownload(row.licenseId);
						}}
						disabled={getDerivedStatus(row) !== "ACTIVE"}
						className={`inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg font-medium transition ${getDerivedStatus(row) !== "ACTIVE"
							? "bg-slate-200 text-slate-500 cursor-not-allowed"
							: "bg-brand text-white hover:bg-brand-dark"
							}`}
					>
						<>
							<Download size={14} />
							Download
						</>
					</button>

					{/* An edit re-signs the license, and the generator can only
					    re-sign a license that still has time left on it. */}
					<button
						onClick={(e) => {
							e.stopPropagation();
							if (onEdit) onEdit(row);
						}}
						disabled={getDerivedStatus(row) !== "ACTIVE"}
						title={
							getDerivedStatus(row) === "ACTIVE"
								? "Edit entitlements"
								: "Expired licenses cannot be edited — delete and re-issue"
						}
						className={`inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg font-medium transition ${getDerivedStatus(row) !== "ACTIVE"
							? "bg-slate-200 text-slate-500 cursor-not-allowed"
							: "hover:opacity-80"
							}`}
						style={
							getDerivedStatus(row) === "ACTIVE"
								? { color: "var(--brand)", border: "1px solid var(--brand)" }
								: undefined
						}
					>
						<Pencil size={14} />
					</button>

					{/* Enabled for expired and superseded rows too — those are
					    exactly the ones worth clearing out. */}
					<button
						onClick={(e) => {
							e.stopPropagation();
							if (onDelete) onDelete(row);
						}}
						title="Delete license"
						className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg font-medium transition hover:opacity-80"
						style={{ color: "var(--danger)", border: "1px solid var(--danger)" }}
					>
						<Trash2 size={14} />
					</button>
				</div>
			),
		},
	];

	const prepareForm = async () => {
		try {
			const clientQuery = buildQueryString({
				page: 1,
				limit: CLIENT_DROPDOWN_LIMIT,
			});

			const [clientsRes, configRes] = await Promise.all([
				callApi(
					API_CONFIG.CLIENT.GET.METHOD,
					`${API_CONFIG.CLIENT.GET.ENDPOINT}?${clientQuery}`
				),
				callApi(API_CONFIG.TRINETRA_LICENSE.CONFIG.METHOD, API_CONFIG.TRINETRA_LICENSE.CONFIG.ENDPOINT),
			]);

			const clients = clientsRes?.data?.data || [];
			const cfg = configRes?.data?.data || {};
			const config = {
				aiModels: cfg.availableModels || [],
				features: cfg.availableFeatures || [],
				featureCatalog: cfg.featureCatalog || [],
				reservedParameterKeys: cfg.reservedParameterKeys || [],
			};

			return { ok: true, data: { clients, config } };
		} catch (error) {
			showError(getReadableErrorMessage(error));
			return { ok: false, error };
		}
	};

	const submitForm = async (payload) => {
		try {
			await callApi(
				API_CONFIG.TRINETRA_LICENSE.CREATE.METHOD,
				API_CONFIG.TRINETRA_LICENSE.CREATE.ENDPOINT,
				payload
			);
			showSuccess("Trinetra License generated successfully");
			return { ok: true };
		} catch (error) {
			showError(getReadableErrorMessage(error));
			return { ok: false, error };
		}
	};

	/**
	 * Re-signs an existing license with new entitlements.
	 *
	 * A PUT rather than a second POST because the license is not replaced: the
	 * same row, ID and hardware binding survive the edit, so issuing one over
	 * the create endpoint would be rejected for double-licensing the machine.
	 */
	const submitEdit = async (licenseId, payload) => {
		try {
			const res = await callApi(
				API_CONFIG.TRINETRA_LICENSE.UPDATE.METHOD,
				API_CONFIG.TRINETRA_LICENSE.UPDATE.ENDPOINT.replace(":licenseId", licenseId),
				payload
			);
			showSuccess(
				res?.data?.message ||
				"Trinetra License updated successfully"
			);
			return { ok: true };
		} catch (error) {
			showError(getReadableErrorMessage(error));
			return { ok: false, error };
		}
	};

	const fetchList = async ({
		search,
		licenseType,
		status,
		allowedModels,
		features,
		page = 1,
		limit = PAGE_SIZE,
	}) => {
		try {
			const query = buildQueryString({
				page,
				limit,
				search: search || undefined,
				licenseType:
					licenseType && licenseType !== "all"
						? licenseType
						: undefined,
				status:
					status && status !== "all"
						? status
						: undefined,
				allowedModels:
					allowedModels && allowedModels !== "all"
						? allowedModels
						: undefined,
				features:
					features && features !== "all"
						? features
						: undefined,
			});

			const res = await callApi(
				API_CONFIG.TRINETRA_LICENSE.LIST.METHOD,
				`${API_CONFIG.TRINETRA_LICENSE.LIST.ENDPOINT}?${query}`
			);

			return {
				rows: res.data.data || [],
				total: res.data.total || 0,
			};
		} catch (error) {
			showError(getReadableErrorMessage(error));
			return { rows: [], total: 0, error };
		}
	};

	const renderForm = ({ onSubmit, isLoading, formData, mode = "create", license = null }) => {
		const clients = formData?.clients || [];
		const config = formData?.config || {
			aiModels: [],
			features: [],
			featureCatalog: [],
			reservedParameterKeys: [],
		};

		return (
			<TrinetraLicenseGenerationForm
				// The form seeds its state from `license` once, on mount. Keying
				// on the license forces a fresh mount when a different one is
				// opened, so it can never show the previous row's values.
				key={license?.licenseId || "create"}
				onSubmit={onSubmit}
				isLoading={isLoading}
				clients={clients}
				config={config}
				mode={mode}
				license={license}
			/>
		);
	};

	return {
		loadFilterData,
		prepareForm,
		submitForm,
		submitEdit,
		renderForm,
		fetchList,
		defaultFilters: DEFAULT_FILTERS,
		getFilterDefinitions,
		tableColumns: columns,
	};
};
